import React, { useEffect, useState } from 'react';

import { IconButton, Spinner } from 'components';
import Modal from 'components/Modal';
import { ButtonV3 } from 'components/ComponentV2';
import Avatar from 'components/Avatar';
import { useResponsive } from 'hooks/useResponsive';
import { ParticipantAttributes } from 'lib/models/participant';
import { RoomAttributes } from 'lib/models/candidate-messages';
import { OpportunityResponse } from 'lib/models/opportunity';
import { parseResponse } from 'lib/utils/parser';
import { useCandidateMessage } from 'lib/providers/CandidateMessageProvider';

import styles from './ParticipantsListModal.module.scss';

interface ParticipantsListModalProps {
  open: boolean;
  activeRoom: RoomAttributes | null;
  onClose: () => void;
}

export const ParticipantsListModal: React.FC<ParticipantsListModalProps> = (
  props: ParticipantsListModalProps
) => {
  const { open, activeRoom, onClose } = props;
  const screens = useResponsive();

  const { getParticipants, isLoading } = useCandidateMessage();

  const [participantsData, setParticipantsData] = useState<
    ParticipantAttributes[]
  >([]);

  useEffect(() => {
    if (open && activeRoom) {
      getParticipantsData();
    } else {
      setParticipantsData([]);
    }
  }, [open]);

  const getParticipantsData = async () => {
    const data = await getParticipants(activeRoom?.id || '');
    setParticipantsData(data || []);
  };

  const isHost = (participant: ParticipantAttributes) => {
    if (activeRoom) {
      const { creatable_for } = parseResponse(
        activeRoom.receiver as OpportunityResponse
      );
      return creatable_for.data.attributes.id === participant.participable_id;
    }
  };

  const isCoHost = (participant: ParticipantAttributes) => {
    if (activeRoom) {
      const { cohost_application } = parseResponse(
        activeRoom.receiver as OpportunityResponse
      );
      return (
        cohost_application &&
        cohost_application.data.attributes.cohost_id ===
          participant.participable_id
      );
    }
  };

  return (
    <Modal
      isOpen={open}
      contentClassName={styles.participantsListModal}
      onRequestClose={onClose}
    >
      {isLoading && <Spinner wrapper size="small" />}
      {!isLoading && (
        <>
          <div className={styles.participantsListModalHeader}>
            <p>Participants</p>
            <IconButton
              className={styles.closeIcon}
              iconName="close"
              size="small"
              onClick={onClose}
            />
          </div>
          <div className={styles.participantsListModalContent}>
            <div className={styles.participantsContainer}>
              {participantsData.map(
                (participant: ParticipantAttributes, index: number) => (
                  <>
                    <div className={styles.card}>
                      <div className={styles.details}>
                        <Avatar
                          size="medium"
                          name={participant.name}
                          icon={participant.icon_url}
                        />
                        <div className={styles.participantInfo}>
                          <p className={styles.title}>{participant.name}</p>
                          <p className={styles.caption}>
                            {participant.description}
                          </p>
                        </div>
                      </div>
                      {isHost(participant) && (
                        <div className={styles.host}>
                          <p>Host</p>
                        </div>
                      )}
                      {isCoHost(participant) && (
                        <div className={styles.host}>
                          <p>Co-host</p>
                        </div>
                      )}
                    </div>
                    {index !== participantsData.length - 1 && (
                      <div className={styles.divider}></div>
                    )}
                  </>
                )
              )}
            </div>
          </div>
          {!screens.sm && (
            <div className={styles.participantsListModalAction}>
              <ButtonV3 onClick={onClose}>Done</ButtonV3>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};
